import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_PREFIX = 'scroll::';
const MAX_RESTORE_ATTEMPTS = 12;
const SAVE_ENABLE_DELAY = 300;

const ScrollToTop = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const storageKey = `${STORAGE_PREFIX}${location.pathname}${location.search}`;

  const restoreAttempts = useRef(0);
  const scrollSaveFrameId = useRef(null);
  const isRestoringRef = useRef(false);
  const canSaveRef = useRef(false);
  const lastSavedPositionRef = useRef(0);

  // disable native browser restore
  useLayoutEffect(() => {
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = 'auto';
    };
  }, []);

  // MAIN SCROLL RESTORE EFFECT
  useEffect(() => {
    let animationFrameId;
    let enableSaveTimeout;

    restoreAttempts.current = 0;
    isRestoringRef.current = true;
    canSaveRef.current = false;

    const savedPosition = parseInt(sessionStorage.getItem(storageKey) || "0", 10);

    const targetPosition =
      navigationType === "POP" ? savedPosition : 0; // 🔥 KEY FIX: only POP restores

    lastSavedPositionRef.current = targetPosition;

    const restoreScrollPosition = () => {
      restoreAttempts.current += 1;

      animationFrameId = window.requestAnimationFrame(() => {
        const maxScrollable =
          document.documentElement.scrollHeight - window.innerHeight;

        if (
          targetPosition > maxScrollable &&
          restoreAttempts.current <= MAX_RESTORE_ATTEMPTS
        ) {
          restoreScrollPosition();
          return;
        }

        window.scrollTo({ top: targetPosition, behavior: "auto" });

        enableSaveTimeout = setTimeout(() => {
          isRestoringRef.current = false;
          canSaveRef.current = true;
        }, SAVE_ENABLE_DELAY);
      });
    };

    restoreScrollPosition();

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (enableSaveTimeout) clearTimeout(enableSaveTimeout);

      // Save latest position
      sessionStorage.setItem(storageKey, lastSavedPositionRef.current.toString());
    };
  }, [storageKey, navigationType]);

  // Scroll Save While Scrolling
  useEffect(() => {
    const saveScrollPosition = () => {
      if (isRestoringRef.current || !canSaveRef.current) {
        scrollSaveFrameId.current = null;
        return;
      }

      const position = window.scrollY || 0;
      lastSavedPositionRef.current = position;
      sessionStorage.setItem(storageKey, position.toString());
      scrollSaveFrameId.current = null;
    };

    const handleScroll = () => {
      if (isRestoringRef.current || !canSaveRef.current) return;

      if (scrollSaveFrameId.current)
        cancelAnimationFrame(scrollSaveFrameId.current);

      scrollSaveFrameId.current = requestAnimationFrame(saveScrollPosition);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollSaveFrameId.current) {
        cancelAnimationFrame(scrollSaveFrameId.current);
        saveScrollPosition();
      }
    };
  }, [storageKey]);

  return null;
};

export default ScrollToTop;
export { ScrollToTop };
